import errorMessages from '@/shared/errorMessages.json'

import {
  getContractProductsAndPrices,
  getCurrency,
  getPriceElements,
  mapContractItemToGTM,
} from './gtm/gtmHelper'
import type { GTMItemType } from './sendGTMEvent'
import { contractItemsEvent, sendGTMEvent } from './sendGTMEvent'
import type { PriceAreaCode } from '../graphql/schema/commonBackend/graphql'
import type { CampaignConfigurationFragmentFragment } from '../graphql/schema/contentful/graphql'
import type { EnrichedContractProduct } from '../services/campaignDataResolver'
import type { DeliveryAddressType } from '../store/slices/checkoutFlowSlice'

interface ButtonClickGTMEventProps {
  click_element: string
  click_text: string
  logged_in?: string
}

interface GraphInteractionGTMEventProps {
  graph_title: string
  click_element: string
  click_text: string
}

interface ErrorGTMEventProps {
  section: string
  error_type: string
  element_title?: string
  error_message?: string
  error_code?: string
}

interface CheckoutErrorGTMEventProps {
  isMultipleEditError: boolean
  isOrderErrorOpened: boolean
}

interface ContractItemsGTMEventProps {
  listId: string
  title?: string | null
  deliveryArea?: PriceAreaCode
  campaign_name?: string
  items: GTMItemType[]
}

interface MappedContractItemsGTMEventProps {
  contractProducts: EnrichedContractProduct[]
  defaultCampaignId?: number | null
  campaignsConfigurations?: CampaignConfigurationFragmentFragment[]
  estimatedConsumption?: number
  deliveryStartDate?: string
  deliveryArea?: PriceAreaCode
  deliveryAddressType?: DeliveryAddressType
  listId: string
  title?: string | null
}

export const buttonClickGTMEvent = ({
  click_element,
  click_text,
  logged_in,
}: ButtonClickGTMEventProps) => {
  sendGTMEvent({
    event: 'click_event',
    click_element,
    click_text,
    logged_in: logged_in || undefined,
  })
}

export const graphInteractionGTMEvent = ({
  graph_title,
  click_element,
  click_text,
}: GraphInteractionGTMEventProps) => {
  sendGTMEvent({
    event: 'graph_interaction',
    graph_title,
    click_element,
    click_text,
  })
}

export const errorGTMEvent = ({
  section,
  error_type,
  element_title,
  error_message,
  error_code,
}: ErrorGTMEventProps) => {
  sendGTMEvent({
    event: 'error_event',
    section,
    error_type,
    element_title,
    error_message,
    error_code,
  })
}

export const checkoutErrorGTMEvent = ({
  isMultipleEditError,
  isOrderErrorOpened,
}: CheckoutErrorGTMEventProps) => {
  const gtmValues = {
    section: 'checkout',
    error_type: 'error_input',
  }

  if (isMultipleEditError) {
    errorGTMEvent({
      ...gtmValues,
      element_title: errorMessages['CheckoutNotificationErrorMessage'],
      error_message: errorMessages['CheckoutNotificationErrorMessage'],
    })
  }
  if (isOrderErrorOpened) {
    errorGTMEvent({
      ...gtmValues,
      element_title: errorMessages['CheckoutOrderErrorMessage'],
      error_message: errorMessages['CheckoutOrderErrorMessage'],
    })
  }
}

export const contractItemsGTMEvent = ({
  listId,
  title,
  deliveryArea,
  campaign_name,
  items,
}: ContractItemsGTMEventProps) => {
  contractItemsEvent(
    'view_item_list',
    {
      item_list_id: listId,
      item_list_name: title,
      currency: getCurrency(deliveryArea),
      campaign_name,
    },
    items,
  )
}

export const mappedContractItemsGTMEvent = ({
  contractProducts,
  defaultCampaignId,
  campaignsConfigurations,
  estimatedConsumption,
  deliveryStartDate,
  deliveryArea,
  deliveryAddressType,
  listId,
  title,
}: MappedContractItemsGTMEventProps) => {
  const products = getContractProductsAndPrices(
    contractProducts,
    defaultCampaignId,
    campaignsConfigurations,
    estimatedConsumption,
    deliveryStartDate,
    deliveryArea,
  )

  const mappedContractItem = products.map((product) => {
    const priceElements = getPriceElements(
      product.contractProduct,
      deliveryArea,
      estimatedConsumption,
      [],
      deliveryStartDate,
    )
    return mapContractItemToGTM(product.contractProduct, priceElements, deliveryAddressType)
  })

  contractItemsGTMEvent({
    listId,
    title,
    deliveryArea,
    items: mappedContractItem,
  })
}
